/**
 * Fechas del calendario de QUIEN MIRA, no del de Greenwich.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ NO `toISOString().slice(0, 10)`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `toISOString()` pasa la hora a UTC. En Caracas (UTC−4), a las ocho de la
 * noche del día 5 devuelve el día 6: el filtro «hasta hoy» de Gastos,
 * Cotizaciones o el Registro de ventas empezaba a pedir el día de mañana, y
 * «este mes» se corría un día el primero de cada mes. Justo a la hora en que se
 * cierra el negocio y se miran las cuentas. Lo encontró la auditoría, en cinco
 * pantallas que lo hacían cada una por su lado.
 *
 * Aquí se arma la fecha a mano con el año, mes y día del reloj del aparato.
 * (`FiltroDeFechas` y el Reporte detallado ya lo hacían bien: son la misma idea.)
 */

/** `2026-09-05`, con el día del calendario de este aparato. */
export function fechaLocal(f: Date = new Date()): string {
  const mes = String(f.getMonth() + 1).padStart(2, "0");
  const dia = String(f.getDate()).padStart(2, "0");
  return `${f.getFullYear()}-${mes}-${dia}`;
}

/**
 * El primer instante de ese día EN ESTE APARATO, con su zona escrita:
 * `inicioDelDia("2026-09-16")` → `"2026-09-16T00:00:00.000-04:00"` en Caracas.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LAS LISTAS PIDEN SU RANGO CON LA ZONA DEL APARATO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Se mandaba «2026-09-16T23:59:59» a secas, y una hora sin zona el servidor no
 * sabe de dónde es. Tres cotizaciones de las 9:30 pm no salían en la lista del
 * día. Decidido con el dueño: el día es el del aparato de quien mira, así que se
 * manda con su zona y el servidor lo respeta tal cual.
 */
export function inicioDelDia(fecha: string): string {
  return conLaZonaDelAparato(fecha, 0, 0, 0, 0);
}

/** El último instante de ese día en este aparato, con su zona. Ver `inicioDelDia`. */
export function finDelDia(fecha: string): string {
  return conLaZonaDelAparato(fecha, 23, 59, 59, 999);
}

/**
 * Un día y una hora de reloj de ESTE aparato, con su zona:
 * `instanteLocal("2026-10-30", "20:30")` → `"2026-10-30T20:30:00.000-04:00"`.
 * Es como se manda la hora de una reservación (ver `inicioDelDia`).
 */
export function instanteLocal(fecha: string, hora: string): string {
  const [h, m] = hora.split(":").map(Number);
  return conLaZonaDelAparato(fecha, h ?? 0, m ?? 0, 0, 0);
}

function conLaZonaDelAparato(
  fecha: string,
  hora: number,
  minuto: number,
  segundo: number,
  milesimas: number,
): string {
  const [a, m, d] = fecha.split("-").map(Number);
  // La zona de ESE día y no la de hoy: donde hay horario de verano, cambia.
  const instante = new Date(a!, (m ?? 1) - 1, d ?? 1, hora, minuto, segundo, milesimas);
  const minutos = -instante.getTimezoneOffset();
  const signo = minutos >= 0 ? "+" : "-";
  const dos = (n: number) => String(Math.floor(n)).padStart(2, "0");
  const reloj = `${dos(hora)}:${dos(minuto)}:${dos(segundo)}.${String(milesimas).padStart(3, "0")}`;
  return `${fecha}T${reloj}${signo}${dos(Math.abs(minutos) / 60)}:${dos(Math.abs(minutos) % 60)}`;
}

/** La fecha de hace `n` días, en el calendario de este aparato. */
export function haceDias(n: number): string {
  const f = new Date();
  f.setDate(f.getDate() - n);
  return fechaLocal(f);
}
